/** First-launch start flow (owned by Lane 3): welcome + difficulty, the approved P707 campaign, or a P706 catalog scenario; no path pick. */
import { useState } from 'preact/hooks';
import { useStore } from '../store';
import { fmtMoney } from '../bridge';
import { PathCards } from './PathCards';
import scenariosJson from '../../../data/scenarios.json';
import campaignJson from '../../../data/campaign.json';

/** P706 catalog (data/scenarios.json). Only the three original P706 scenarios are player-selectable;
    the rest of the catalog stays in the data untouched (several entries are PROPOSED drafts) until the
    owner approves them - a missing PROPOSED marker is not approval. */
interface ScenarioEntry { id: string; name: string; description: string; start_mode: string; time_limit_days?: number }
const P706_SELECTABLE = new Set(['first_ranch', 'oryx_warden', 'big_country']);
const SCENARIOS = (scenariosJson.scenarios as unknown as ScenarioEntry[]).filter((sc) => P706_SELECTABLE.has(sc.id)).sort((a, b) => a.name.localeCompare(b.name));

/** Flavor per balance.json start_modes (cash read from the live catalog copy). */
const MODE_BLURB: Record<string, { name: string; cashCents: number; text: string }> = {
  easy: { name: 'Easy', cashCents: 25_000_000, text: 'A generous grant, two enclosures and a keeper. Learn the ropes.' },
  standard: { name: 'Standard', cashCents: 10_000_000, text: 'A modest grant, one pen and a small loan. The intended climb.' },
  hard: { name: 'Hard', cashCents: 3_500_000, text: 'Thin cash, real debt. Every parcel and animal has to earn.' },
};

/** P707 campaign chain (data/campaign.json). Order and display names approved
    by Matthew 2026-10-23 (order question, option A: Raven Creek (First Ranch)
    -> Cedar Breaks (Oryx Warden) -> Salt Flat Country (Big Country)). Only the
    approved labels show; there is no approved intro text. */
interface CampaignEntry { id: string; scenario: string; name?: string }
const CAMPAIGN = (campaignJson.reserves as unknown as CampaignEntry[]).map((r) => {
  const sc = (scenariosJson.scenarios as unknown as ScenarioEntry[]).find((x) => x.id === r.scenario);
  return { id: r.id, name: r.name ?? sc?.name ?? r.scenario, startMode: sc?.start_mode ?? 'standard' };
});

export function StartFlow() {
  const { sim, beginPlay, beginCampaign } = useStore();
  const MODE_ORDER = ['easy', 'standard', 'hard'];
  const rawModes = sim.startModes.length ? sim.startModes : ['easy', 'standard', 'hard'];
  // Present gentlest-first regardless of sim order so the preselected default reads first.
  const modes = [...rawModes].sort((x, y) => {
    const ix = MODE_ORDER.indexOf(x); const iy = MODE_ORDER.indexOf(y);
    return (ix === -1 ? MODE_ORDER.length : ix) - (iy === -1 ? MODE_ORDER.length : iy);
  });
  const [mode, setMode] = useState<string>('standard');
  const [scenario, setScenario] = useState<string | null>(null);
  const picked = scenario === null ? null : SCENARIOS.find((sc) => sc.id === scenario) ?? null;
  return (
    <div class="start-flow">
      <div class="start-flow-inner">
        <div class="eyebrow eyebrow-rule">Welcome to</div>
        <h1 class="display" style="font-size:30px;text-align:center;margin:6px 0 2px">Exotics Adventure</h1>
        <div class="eyebrow eyebrow-rule" style="margin-bottom:14px">Your reserve, your story</div>

        <div class="eyebrow eyebrow-rule">Choose your start</div>
        <div style="display:flex;gap:8px;margin-top:8px">
          {modes.map((m) => {
            const b = MODE_BLURB[m] ?? { name: m, cashCents: 0, text: '' };
            const cashCents = sim.startModeCash[m] ?? b.cashCents; // live catalog wins
            return (
              <button
                key={m}
                class="card"
                aria-pressed={scenario === null && mode === m}
                style={`flex:1;padding:10px 8px;text-align:center;cursor:pointer;background:var(--ui-parchment-soft);border:var(--ui-border);${scenario === null && mode === m ? 'outline:2px solid var(--ui-amber);' : ''}`}
                onClick={() => { setMode(m); setScenario(null); }}
              >
                <div class="display" style="font-size:12.5px;overflow-wrap:break-word">{b.name}</div>
                {cashCents > 0 && <div class="row-value" style="font-size:13px;margin-top:2px">${fmtMoney(cashCents)}</div>}
                <div class="row-label" style="text-transform:none;letter-spacing:0.01em;margin-top:4px;font-size:10px">{b.text}</div>
              </button>
            );
          })}
        </div>

        <div class="eyebrow eyebrow-rule" style="margin-top:16px">Or take on a scenario</div>
        <div style="margin-top:8px;display:grid;gap:6px">
          {SCENARIOS.map((sc) => (
            <button
              key={sc.id}
              class="card"
              aria-pressed={scenario === sc.id}
              style={`padding:8px 10px;text-align:left;cursor:pointer;background:var(--ui-parchment-soft);border:var(--ui-border);${scenario === sc.id ? 'outline:2px solid var(--ui-amber);' : ''}`}
              onClick={() => { setScenario(scenario === sc.id ? null : sc.id); }}
            >
              <div class="row-value" style="font-size:14px">{sc.name}</div>
              <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em">{sc.description}</div>
              <div class="row-label" style="margin-top:2px">Starts on {sc.start_mode}{sc.time_limit_days !== undefined ? ` - ${String(sc.time_limit_days)} days` : ' - no time limit'}</div>
            </button>
          ))}
        </div>

        <div class="eyebrow eyebrow-rule" style="margin-top:16px">Or play the campaign</div>
        <div class="card" style="margin-top:8px;padding:10px 12px;background:var(--ui-parchment-soft);border:var(--ui-border)">
          {CAMPAIGN.map((r, i) => (
            <div key={r.id} style={`display:flex;align-items:baseline;gap:8px;${i > 0 ? 'margin-top:6px' : ''}`}>
              <span class="row-label">{i + 1}.</span>
              <div>
                <div class="row-value" style="font-size:14px">{r.name}</div>
                <div class="row-label">Starts on {r.startMode}</div>
              </div>
            </div>
          ))}
          <button
            class="btn btn-primary"
            style="width:100%;margin-top:10px"
            onClick={() => { beginCampaign(CAMPAIGN[0]?.id ?? 'reserve_raven_creek', CAMPAIGN[0]?.startMode ?? 'easy'); }}
          >Begin the campaign</button>
        </div>

        <div class="eyebrow eyebrow-rule" style="margin-top:16px">What you can become</div>
        <PathCards compact />
        <button class="btn btn-primary" style="width:100%;margin-top:14px" onClick={() => { if (picked) beginPlay(picked.start_mode, picked.id); else beginPlay(mode); }}>{picked ? `Begin: ${picked.name}` : 'Begin'}</button>
      </div>
    </div>
  );
}
